import Dexie, { Table } from 'dexie';
import {
  Site,
  Asset,
  FormTemplate,
  Inspection,
  User,
  Organization,
  FunctionalLocation,
  MeasurementDefinition,
} from '@easydone/shared';

/**
 * Sync queue item for tracking pending operations
 */
export interface SyncQueueItem {
  id?: number;
  type: 'create' | 'update' | 'delete';
  collection: 'sites' | 'assets' | 'inspections' | 'templates';
  docId: string;
  data?: any;
  timestamp: number;
  attempts: number;
  lastError?: string;
}

/**
 * Local database using Dexie (IndexedDB wrapper)
 */
export class EasyDoneDB extends Dexie {
  // Tables
  sites!: Table<Site, string>;
  assets!: Table<Asset, string>;
  flocs!: Table<FunctionalLocation, string>;
  templates!: Table<FormTemplate, string>;
  inspections!: Table<Inspection, string>;
  users!: Table<User, string>;
  organization!: Table<Organization, string>;
  measurementDefinitions!: Table<MeasurementDefinition, string>;
  syncQueue!: Table<SyncQueueItem, number>;

  constructor() {
    super('EasyDoneDB');

    // Define schema - Version 1 (original)
    this.version(1).stores({
      sites: 'id, orgId, name, code, type, status',
      assets: 'id, orgId, siteId, name, assetId, type, status',
      templates: 'id, orgId, name, category, status',
      inspections: 'id, orgId, siteId, assetId, templateId, status, createdAt, syncStatus',
      users: 'id, orgId, email, role',
      organization: 'id',
      syncQueue: '++id, timestamp, collection, type',
    });

    // Version 2 - Add FLOCs and Measurement Definitions
    this.version(2).stores({
      sites: 'id, orgId, name, code, type, status',
      assets: 'id, orgId, currentFlocId, name, assetId, type, status',
      flocs: 'id, orgId, code, name, type, status',
      templates: 'id, orgId, name, category, status',
      inspections: 'id, orgId, flocId, assetId, templateId, status, createdAt, syncStatus, reservedBy',
      users: 'id, orgId, email, role',
      organization: 'id',
      measurementDefinitions: 'id, orgId, name, category, context',
      syncQueue: '++id, timestamp, collection, type',
    });
  }

  /**
   * Clear all local data (for logout)
   */
  async clearAll(): Promise<void> {
    await Promise.all([
      this.sites.clear(),
      this.assets.clear(),
      this.flocs.clear(),
      this.templates.clear(),
      this.inspections.clear(),
      this.users.clear(),
      this.organization.clear(),
      this.measurementDefinitions.clear(),
      this.syncQueue.clear(),
    ]);
  }

  /**
   * Get database size estimate
   */
  async getStorageEstimate(): Promise<{ usage: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
      };
    }
    return { usage: 0, quota: 0 };
  }

  /**
   * Check if offline mode is available
   */
  async isOfflineAvailable(): Promise<boolean> {
    try {
      const [sitesCount, assetsCount, templatesCount] = await Promise.all([
        this.sites.count(),
        this.assets.count(),
        this.templates.count(),
      ]);
      return sitesCount > 0 && assetsCount > 0 && templatesCount > 0;
    } catch (error) {
      console.error('Failed to check offline availability:', error);
      return false;
    }
  }
}

// Create singleton instance
export const db = new EasyDoneDB();
